import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  getGoals, 
  createGoal, 
  updateGoal, 
  deleteGoal,
  formatCurrency
} from '../lib/api';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Target,
  Palmtree,
  GraduationCap,
  Home,
  Car,
  PiggyBank,
  Plane,
  Gift,
  ShieldCheck
} from 'lucide-react';

const GOAL_ICONS = {
  'target': Target,
  'palmtree': Palmtree,
  'graduation-cap': GraduationCap,
  'home': Home,
  'car': Car,
  'piggy-bank': PiggyBank,
  'plane': Plane,
  'gift': Gift,
  'shield-check': ShieldCheck
};

const GoalsPage = () => {
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  
  // Form
  const [formName, setFormName] = useState('');
  const [formTarget, setFormTarget] = useState('');
  const [formCurrent, setFormCurrent] = useState('');
  const [formIcon, setFormIcon] = useState('target');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getGoals();
      setGoals(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingGoal(null);
    setFormName('');
    setFormTarget('');
    setFormCurrent('0');
    setFormIcon('target');
    setDialogOpen(true);
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setFormName(goal.name);
    setFormTarget(goal.target_amount.toString());
    setFormCurrent(goal.current_amount.toString());
    setFormIcon(goal.icon || 'target');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName || !formTarget) {
      toast.error('Compila tutti i campi');
      return;
    }

    const data = {
      name: formName,
      target_amount: parseFloat(formTarget),
      current_amount: parseFloat(formCurrent) || 0,
      icon: formIcon
    };

    try {
      if (editingGoal) {
        await updateGoal(editingGoal.id, data);
        toast.success('Obiettivo aggiornato');
      } else {
        await createGoal(data);
        toast.success('Obiettivo creato');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!goalToDelete) return;
    
    try {
      await deleteGoal(goalToDelete.id);
      toast.success('Obiettivo eliminato');
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const addToGoal = async (goal, amount) => {
    try {
      await updateGoal(goal.id, {
        ...goal,
        current_amount: goal.current_amount + amount
      });
      toast.success(`+${formatCurrency(amount)} aggiunti a ${goal.name}`);
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Calculate totals
  const totalTarget = goals.reduce((sum, g) => sum + g.target_amount, 0);
  const totalCurrent = goals.reduce((sum, g) => sum + g.current_amount, 0);
  const overallProgress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

  return (
    <div className="space-y-6 animate-in" data-testid="goals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Obiettivi</h1>
          <p className="text-muted-foreground">Traccia i tuoi obiettivi di risparmio</p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-goal-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Obiettivo
        </Button>
      </div>

      {/* Overall Progress */}
      {goals.length > 0 && (
        <Card data-testid="overall-progress-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Progresso Totale</p>
                <p className="text-3xl font-bold">{formatCurrency(totalCurrent)}</p>
                <p className="text-sm text-muted-foreground">di {formatCurrency(totalTarget)}</p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-primary">{Math.round(overallProgress)}%</p>
              </div>
            </div>
            <Progress value={overallProgress} className="h-3" />
          </CardContent>
        </Card>
      )}

      {/* Goals Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Caricamento...</div>
      ) : goals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nessun obiettivo</h3>
            <p className="text-muted-foreground mb-4">Inizia a tracciare i tuoi obiettivi di risparmio</p>
            <Button onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" /> Crea il primo obiettivo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map((goal) => {
            const percentage = goal.target_amount > 0 
              ? Math.min((goal.current_amount / goal.target_amount) * 100, 100) 
              : 0;
            const IconComponent = GOAL_ICONS[goal.icon] || Target;
            const isComplete = goal.current_amount >= goal.target_amount;

            return (
              <Card key={goal.id} className={`card-hover ${isComplete ? 'border-primary' : ''}`} data-testid={`goal-card-${goal.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isComplete ? 'bg-primary text-primary-foreground' : 'bg-primary/10'}`}>
                        <IconComponent className={`w-6 h-6 ${isComplete ? '' : 'text-primary'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{goal.name}</h3>
                        {isComplete && (
                          <span className="text-xs text-primary font-medium">Completato!</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(goal)}
                        data-testid={`edit-goal-${goal.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setGoalToDelete(goal); setDeleteDialogOpen(true); }}
                        data-testid={`delete-goal-${goal.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-2xl font-bold tabular-nums">{formatCurrency(goal.current_amount)}</span>
                      <span className="text-muted-foreground">{Math.round(percentage)}%</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Obiettivo: {formatCurrency(goal.target_amount)}</span>
                      <span>{formatCurrency(goal.target_amount - goal.current_amount)} rimanenti</span>
                    </div>

                    {/* Quick add buttons */}
                    {!isComplete && (
                      <div className="flex gap-2 pt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => addToGoal(goal, 50)}
                          data-testid={`add-50-${goal.id}`}
                        >
                          +€50
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => addToGoal(goal, 100)}
                          data-testid={`add-100-${goal.id}`}
                        >
                          +€100
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => addToGoal(goal, 200)}
                          data-testid={`add-200-${goal.id}`}
                        >
                          +€200
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingGoal ? 'Modifica Obiettivo' : 'Nuovo Obiettivo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="es. Fondo Emergenza"
                required
                data-testid="goal-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Obiettivo (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formTarget}
                onChange={(e) => setFormTarget(e.target.value)}
                placeholder="0.00"
                required
                data-testid="goal-target-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Importo attuale (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formCurrent}
                onChange={(e) => setFormCurrent(e.target.value)}
                placeholder="0.00"
                data-testid="goal-current-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Icona</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(GOAL_ICONS).map(([key, Icon]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormIcon(key)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      formIcon === key 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    data-testid={`icon-${key}`}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" data-testid="save-goal-btn">
                {editingGoal ? 'Salva Modifiche' : 'Crea Obiettivo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-goal-dialog">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
          </DialogHeader>
          <p>Sei sicuro di voler eliminare l'obiettivo "{goalToDelete?.name}"?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-goal-btn">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoalsPage;
